// @mui
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Stack, { StackProps } from '@mui/material/Stack'
// types

// components
import Iconify from 'src/components/iconify'
import { shortDateLabel } from 'src/utils/utils'
import { IOrderTableFilterValue, IOrderTableFilters } from 'src/types/orders'

// ----------------------------------------------------------------------

type Props = StackProps & {
  filters: IOrderTableFilters
  onFilters: (name: string, value: IOrderTableFilterValue) => void
  onResetFilters: VoidFunction
  results: number
}

export default function OrderTableFiltersResult({
  filters,
  onFilters,
  onResetFilters,
  results,
  ...other
}: Props) {
  const handleRemoveEvent = () => {
    onFilters('event', null)
  }

  const handleRemoveCategories = () => {
    onFilters('categories', null)
  }
  const handleRemovePaymentType = (inputValue: string) => {
    const newValue = filters.paymentType?.filter((item) => item !== inputValue)
    onFilters('paymentType', newValue)
  }

  const handleRemoveSalers = (inputValue: string) => {
    const newValue = filters.salers?.filter((item) => item !== inputValue)
    onFilters('salers', newValue)
  }

  const handleRemoveDate = () => {
    onFilters('dateStart', null)
    onFilters('dateEnd', null)
  }

  const handleRemoveTerminalSerial = () => {
    onFilters('terminalSerial', '')
  }

  const shortLabel = shortDateLabel(filters.dateStart, filters.dateEnd)

  return (
    <Stack spacing={1.5} {...other}>
      <Box sx={{ typography: 'body2' }}>
        <strong>{results}</strong>
        <Box component='span' sx={{ color: 'text.secondary', ml: 0.25 }}>
          resultados encontrados
        </Box>
      </Box>

      <Stack flexGrow={1} spacing={1} direction='row' flexWrap='wrap' alignItems='center'>
        {!!filters.event && (
          <Block label='Evento:'>
            <Chip
              key={filters.event?.value}
              label={filters.event?.label}
              size='small'
              onDelete={() => handleRemoveEvent()}
            />
          </Block>
        )}

        {!!filters.categories?.value && (
          <Block label='Categorias:'>
            <Chip
              key={filters.categories?.value}
              label={filters.categories?.label}
              size='small'
              onDelete={() => handleRemoveCategories()}
            />
          </Block>
        )}

        {!!filters.paymentType.length && (
          <Block label='Meios de Pagamento:'>
            {filters.paymentType.map((item) => {
              const paymentType = JSON.parse(item)
              return (
                <Chip
                  key={paymentType?._id}
                  label={paymentType?.label}
                  size='small'
                  onDelete={() => handleRemovePaymentType(item)}
                />
              )
            })}
          </Block>
        )}

        {!!filters.salers.length && (
          <Block label='Vendedores:'>
            {filters.salers.map((item) => (
              <Chip
                key={item?._id}
                label={item?.label}
                size='small'
                onDelete={() => handleRemoveSalers(item)}
              />
            ))}
          </Block>
        )}

        {filters.dateStart && filters.dateEnd && (
          <Block label='Data:'>
            <Chip size='small' label={shortLabel} onDelete={handleRemoveDate} />
          </Block>
        )}

        {filters.terminalSerial && (
          <Block label='Terminal Serial:'>
            <Chip
              size='small'
              label={filters.terminalSerial}
              onDelete={handleRemoveTerminalSerial}
            />
          </Block>
        )}

        <Button
          color='error'
          onClick={onResetFilters}
          startIcon={<Iconify icon='solar:trash-bin-trash-bold' />}
        >
          Limpar
        </Button>
      </Stack>
    </Stack>
  )
}

// ----------------------------------------------------------------------

type BlockProps = StackProps & {
  label: string
}

function Block({ label, children, sx, ...other }: BlockProps) {
  return (
    <Stack
      component={Paper}
      variant='outlined'
      spacing={1}
      direction='row'
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'hidden',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component='span' sx={{ typography: 'subtitle2' }}>
        {label}
      </Box>

      <Stack spacing={1} direction='row' flexWrap='wrap'>
        {children}
      </Stack>
    </Stack>
  )
}
